import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Landmark, IndianRupee, CreditCard, FileText, Loader2, ShieldCheck,
  Paperclip, X, Send, KeyRound, Clock, XCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  createFinancialChange, listMyFinancialChanges, verifyFinancialOtp,
  cancelFinancialChange, uploadKycAttachment,
} from "@lawyer/services/financialApi";

export const FIELDS = [
  { key: "bank_account", label: "Bank account", icon: Landmark, hint: "Where your payouts will be sent." },
  { key: "upi_id", label: "UPI ID", icon: IndianRupee, hint: "Faster payouts under ₹1L." },
  { key: "pan", label: "PAN", icon: CreditCard, hint: "Required for TDS-compliant payouts." },
  { key: "gst", label: "GST number", icon: FileText, hint: "Optional — for GST-registered practices." },
];

const STATUS_LABEL = {
  pending_otp: "Awaiting OTP",
  pending_approval: "Awaiting admin approval",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  expired: "Expired",
};

export function maskDisplay(field, value) {
  if (value == null || value === "") return "Not set";
  if (field === "bank_account" && typeof value === "object") {
    const acc = value.account_number || "";
    const acc_m = acc.length > 4 ? "*".repeat(acc.length - 4) + acc.slice(-4) : acc;
    return `${value.holder_name || "—"} · ${acc_m} · ${value.ifsc || "—"}`;
  }
  const s = String(value);
  if (field === "upi_id" && s.includes("@")) {
    const [l, d] = s.split("@");
    return `${l[0]}${"*".repeat(Math.max(0, l.length - 2))}${l.slice(-1)}@${d}`;
  }
  if (field === "pan" && s.length === 10) return s.slice(0, 4) + "****" + s.slice(-1);
  if (field === "gst" && s.length === 15) return s.slice(0, 4) + "*******" + s.slice(-4);
  return s;
}

export function StatusPill({ status }) {
  const cls = {
    pending_otp: "bg-amber-100 text-amber-800",
    pending_approval: "bg-sky-100 text-sky-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    cancelled: "bg-slate-100 text-slate-500",
    expired: "bg-slate-100 text-slate-500",
  }[status] || "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ---------- Modal ----------
export function ChangeRequestModal({ open, field, currentValue, onClose, onSubmitted }) {
  const meta = FIELDS.find((f) => f.key === field);
  const [step, setStep] = useState("form"); // form | otp | done
  const [busy, setBusy] = useState(false);
  const [reqId, setReqId] = useState(null);
  const [otpChannel, setOtpChannel] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);

  const [bank, setBank] = useState({ account_number: "", ifsc: "", holder_name: "" });
  const [textValue, setTextValue] = useState("");
  const [reason, setReason] = useState("");
  const [attachment, setAttachment] = useState(null); // { id, filename, doc_id }
  const [uploading, setUploading] = useState(false);
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("form");
      setBank({ account_number: "", ifsc: "", holder_name: "" });
      setTextValue("");
      setReason("");
      setAttachment(null);
      setReqId(null);
      setOtp("");
    }
  }, [open]);

  if (!open || !meta) return null;

  const isBank = field === "bank_account";
  const isFirstTime = currentValue == null || currentValue === "";
  const Icon = meta.icon;

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const docType = field === "pan" ? "pan" : "other";
      const r = await uploadKycAttachment(file, docType, `attachment for ${field} change`);
      setAttachment({ id: r.id, filename: file.name });
      toast.success("Attachment uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submitCreate = async () => {
    setBusy(true);
    try {
      const payload = {
        field,
        new_value: isBank ? bank : textValue.trim().toUpperCase(),
        reason: reason.trim() || null,
        attachment_ref: attachment?.id || null,
      };
      // For UPI, don't uppercase — keep as-is
      if (field === "upi_id") payload.new_value = textValue.trim();
      const doc = await createFinancialChange(payload);
      setReqId(doc.id);
      if (doc.status === "pending_approval" || doc.status === "pending_review") {
        // First-time submission: no OTP, straight to Admin review.
        setStep("done");
        toast.success("Sent for admin review");
        onSubmitted?.();
        return;
      }
      setOtpChannel(doc.otp_channel || "");
      setOtpExpiresAt(doc.otp_expires_at);
      setStep("otp");
      toast.success(doc.otp_channel === "sms" ? "OTP sent via SMS" : "OTP sent to your email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit change request");
    } finally { setBusy(false); }
  };

  const submitOtp = async () => {
    if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      await verifyFinancialOtp(reqId, otp);
      setStep("done");
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const bankErrors = {
    account_number: bank.account_number && !/^\d{9,18}$/.test(bank.account_number) ? "Account number must be 9–18 digits (no letters)" : null,
    ifsc: bank.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifsc) ? "IFSC must be 11 characters: 4 letters, a 0, then 6 letters/digits (e.g. HDFC0001234)" : null,
    holder_name: bank.holder_name && bank.holder_name.trim().length < 2 ? "Enter the full account holder name" : null,
  };
  const isFormValid = isBank
    ? /^\d{9,18}$/.test(bank.account_number) && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(bank.ifsc) && bank.holder_name.trim().length >= 2
    : textValue.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="fin-request-modal">
      <div className="max-w-md w-full bg-white rounded-2xl p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-extrabold text-[#0A2540] inline-flex items-center gap-2" style={{ fontFamily: "Poppins" }}>
              <Icon size={16} className="text-[#D4AF37]" /> {isFirstTime ? "Add" : "Update"} {meta.label}
            </h3>
            <p className="text-xs text-slate-500 mt-1">{meta.hint}</p>
          </div>
          <button onClick={onClose} data-testid="fin-modal-close" className="text-slate-400 hover:text-[#0A2540]">
            <X size={18} />
          </button>
        </div>

        {step === "form" && (
          <div className="mt-4 space-y-3">
            {!isFirstTime && (
              <div className="text-xs text-slate-600" data-testid="fin-current-value">
                Current: <strong className="text-[#0A2540]">{maskDisplay(field, currentValue)}</strong>
              </div>
            )}

            {isBank ? (
              <>
                <div>
                  <Label className="text-xs">Account number</Label>
                  <Input
                    value={bank.account_number}
                    onChange={(e) => setBank({ ...bank, account_number: e.target.value.replace(/\s/g, "") })}
                    data-testid="fin-bank-acc"
                    className="mt-1"
                    placeholder="9–18 digits"
                  />
                  {bankErrors.account_number && <p className="mt-1 text-[11px] text-rose-600" data-testid="fin-bank-acc-error">{bankErrors.account_number}</p>}
                </div>
                <div>
                  <Label className="text-xs">IFSC</Label>
                  <Input
                    value={bank.ifsc}
                    onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase().replace(/\s/g, "") })}
                    data-testid="fin-bank-ifsc"
                    className="mt-1"
                    placeholder="HDFC0001234"
                  />
                  {bankErrors.ifsc && <p className="mt-1 text-[11px] text-rose-600" data-testid="fin-bank-ifsc-error">{bankErrors.ifsc}</p>}
                </div>
                <div>
                  <Label className="text-xs">Account holder name (exactly as on your PAN)</Label>
                  <Input
                    value={bank.holder_name}
                    onChange={(e) => setBank({ ...bank, holder_name: e.target.value })}
                    data-testid="fin-bank-holder"
                    className="mt-1"
                    placeholder="Full name as on PAN card"
                  />
                  {bankErrors.holder_name && <p className="mt-1 text-[11px] text-rose-600" data-testid="fin-bank-holder-error">{bankErrors.holder_name}</p>}
                  <p className="mt-1 text-[11px] text-slate-500" data-testid="fin-bank-holder-hint">
                    Must match the name on your PAN — Admin is warned about any mismatch during review.
                  </p>
                </div>
              </>
            ) : (
              <div>
                <Label className="text-xs">{isFirstTime ? meta.label : `New ${meta.label}`}</Label>
                <Input
                  value={textValue}
                  onChange={(e) => setTextValue(field === "upi_id" ? e.target.value : e.target.value.toUpperCase())}
                  data-testid={`fin-${field}-input`}
                  className="mt-1"
                  placeholder={
                    field === "upi_id" ? "e.g. name@ybl"
                      : field === "pan" ? "e.g. ABCDE1234F"
                      : "15-character GSTIN"
                  }
                />
              </div>
            )}

            <div>
              <Label className="text-xs">{isFirstTime ? "Note for the reviewer (optional)" : "Reason (optional)"}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="fin-reason"
                rows={2}
                className="mt-1 rounded-xl"
                placeholder={isFirstTime
                  ? "Anything the reviewer should know — e.g. name on PAN differs slightly from your profile."
                  : "Why are you making this change? Helps the reviewer decide faster."}
              />
            </div>

            <div>
              <Label className="text-xs">Supporting document (optional)</Label>
              <div className="mt-1 flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-dashed border-slate-300 text-xs text-slate-600 cursor-pointer hover:border-[#0A2540]">
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                  {attachment ? "Replace file" : "Attach file"}
                  <input
                    type="file"
                    className="hidden"
                    data-testid="fin-attachment"
                    onChange={(e) => uploadFile(e.target.files?.[0])}
                    accept=".pdf,.png,.jpg,.jpeg"
                  />
                </label>
                {attachment && (
                  <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> {attachment.filename}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {isBank ? "Cancelled cheque or passbook page — PDF or image ≤10 MB." : field === "pan" ? "Photo of your PAN card — PDF or image ≤10 MB." : "Cheque leaf, PAN card, GST certificate — PDF or image ≤10 MB."}
              </p>
              <p className="text-[11px] text-slate-500 mt-2" data-testid="fin-otp-note">
                {isFirstTime
                  ? "Goes straight to Admin for review and approval — no OTP needed for a first submission."
                  : "We\u2019ll send an OTP to your registered mobile to confirm it\u2019s you, then Admin reviews and approves."}
              </p>
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} data-testid="fin-modal-cancel">Cancel</Button>
              <Button
                onClick={submitCreate}
                disabled={busy || !isFormValid}
                data-testid="fin-modal-submit"
                className="rounded-xl adv-btn-primary font-bold"
              >
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                {isFirstTime ? "Submit for review" : "Send OTP"}
              </Button>
            </div>
          </div>
        )}

        {step === "otp" && (
          <div className="mt-4 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
              <KeyRound size={12} className="inline mr-1" />
              A 6-digit code was sent to your {otpChannel === "sms" ? "phone via SMS" : "email"}.
              Enter it below to submit for admin approval. The code expires in 15 minutes.
            </div>
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="fin-otp-input"
              placeholder="123456"
              inputMode="numeric"
              className="text-center tracking-[6px] font-bold text-xl"
            />
            <div className="flex justify-between gap-2 pt-1">
              <Button
                variant="outline"
                onClick={async () => {
                  try { await cancelFinancialChange(reqId); onClose(); toast.info("Request cancelled"); onSubmitted?.(); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                }}
                data-testid="fin-otp-cancel"
              >
                Cancel request
              </Button>
              <Button
                onClick={submitOtp}
                disabled={busy || !/^\d{6}$/.test(otp)}
                data-testid="fin-otp-verify"
                className="rounded-xl adv-btn-primary font-bold"
              >
                {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Verify &amp; submit
              </Button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="mt-4 text-center py-4" data-testid="fin-done">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600 mb-2" />
            <p className="text-sm font-bold text-[#0A2540]">Sent for admin approval</p>
            <p className="text-xs text-slate-500 mt-1">
              An admin will review your request shortly. You&apos;ll be notified on your email and phone once it&apos;s applied.
            </p>
            <Button onClick={onClose} className="mt-4 rounded-xl" data-testid="fin-done-close">Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Main card ----------
// `initialRequests`: page-level prefetched change requests (array, or null on failure) — skips the mount-time fetch.
export default function PayoutKycCard({ profile, initialRequests }) {
  const [openFor, setOpenFor] = useState(null); // field key
  const [requests, setRequests] = useState(Array.isArray(initialRequests) ? initialRequests : []);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listMyFinancialChanges({ limit: 20 });
      setRequests(d.items || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Page-level prefetch supplies the initial list; otherwise self-load. Action handlers call reload() explicitly.
  useEffect(() => {
    if (initialRequests === undefined) reload();
    else if (Array.isArray(initialRequests)) setRequests(initialRequests);
  }, [reload, initialRequests]);

  // Index open requests by field
  const openByField = useMemo(() => {
    const m = {};
    for (const r of requests) {
      if (["pending_otp", "pending_approval"].includes(r.status)) m[r.field] = r;
    }
    return m;
  }, [requests]);

  const currentValueFor = (field) => {
    if (!profile) return null;
    return profile[field];
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="payout-kyc-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base font-extrabold text-[#0A2540] inline-flex items-center gap-2" style={{ fontFamily: "Poppins" }}>
            <ShieldCheck size={16} className="text-[#D4AF37]" /> Payout &amp; KYC
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">Changes require Admin approval and OTP confirmation.</p>
        </div>
        {loading && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>

      <div className="divide-y divide-slate-100">
        {FIELDS.map((f) => {
          const Icon = f.icon;
          const cur = currentValueFor(f.key);
          const pending = openByField[f.key];
          const shown = maskDisplay(f.key, cur);
          const verified = profile?.[`${f.key}_status`] === "verified_by_admin";
          return (
            <div
              key={f.key}
              className="flex items-center justify-between gap-3 py-3 min-w-0"
              data-testid={`payout-row-${f.key}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-slate-500 inline-flex items-center gap-1.5">
                  <Icon size={12} /> {f.label}
                </p>
                <p className={`mt-0.5 text-sm truncate ${shown === "Not set" ? "text-slate-400 italic" : "text-[#0A2540] font-semibold"}`}>{shown}</p>
                {verified && (
                  <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    <CheckCircle2 size={10} /> Verified
                  </p>
                )}
                {pending && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    <Clock size={10} className="inline mr-1" />
                    Requested change to <strong>{pending.new_value_masked}</strong>{" "}
                    <StatusPill status={pending.status} />
                  </p>
                )}
              </div>
              <div className="shrink-0">
                {pending && pending.status === "pending_approval" ? (
                  <span className="text-[11px] font-bold text-slate-500" data-testid={`payout-submitted-${f.key}`}>Awaiting Admin</span>
                ) : pending ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try { await cancelFinancialChange(pending.id); toast.info("Request cancelled"); reload(); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }}
                    data-testid={`payout-cancel-${f.key}`}
                  >
                    <XCircle size={14} className="mr-1" /> Cancel
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setOpenFor(f.key)}
                    data-testid={`payout-request-${f.key}`}
                    className="rounded-xl adv-btn-primary font-bold"
                  >
                    Request change
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ChangeRequestModal
        open={!!openFor}
        field={openFor}
        currentValue={openFor ? currentValueFor(openFor) : null}
        onClose={() => setOpenFor(null)}
        onSubmitted={reload}
      />
    </div>
  );
}
